import { type ExtendedTypesSync, validatorSync as typeValidator } from ".";
import {
  type BaseType,
  type ExtendedTypeValidatorOptions,
  type Props,
  type ValidatorSync,
  isPlainObject,
  typeMismatchError,
} from "./base";
import * as yup from "yup";

export interface ObjectType extends BaseType {
  type: "OBJECT";
  /**
   *  If true, object is considered invalid if it contains unknown properties.
   * @default false
   */
  strict?: boolean;
  /**
   * Shape of properties.
   * @default `{ [key: string]: UnknownType }`
   */
  properties?: { [key: string]: ExtendedTypesSync };
}

export function object(opts?: Props<ObjectType>): ObjectType {
  return {
    ...opts,
    type: "OBJECT",
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function buildPropertyValidators(
  type: Readonly<ObjectType>,
  options?: ExtendedTypeValidatorOptions,
): Record<string, (value: unknown) => void> {
  const validatorMap: Record<string, (value: unknown) => void> = {};
  if (type.properties) {
    for (const [field, propType] of Object.entries(type.properties)) {
      validatorMap[field] = typeValidator(propType, options);
    }
  }
  return validatorMap;
}

export const validator: ValidatorSync<ObjectType> = (type, options) => {
  if (options?.extendedTypesValidationsV2) {
    return handRolledValidator(type, options);
  }
  return yupValidator(type, options);
};

const yupValidator = (
  type: Readonly<ObjectType>,
  options?: ExtendedTypeValidatorOptions,
) => {
  let schema = yup.object();

  if (type.properties) {
    const validatorMap = buildPropertyValidators(type, options);
    schema = schema.test((v) => {
      Object.entries(validatorMap).forEach(([field, validate]) => {
        try {
          validate(v[field]);
        } catch (e) {
          throw new Error(
            `invalid value in property ${field}: ${errorMessage(e)}`,
          );
        }
      });

      if (type.strict) {
        const invalidFields: string[] = [];
        for (const field in v) {
          if (!type.properties?.[field]) invalidFields.push(field);
        }

        if (invalidFields.length)
          throw new Error(
            `unknown object ${
              invalidFields.length === 1 ? "property" : "properties"
            }: ${invalidFields.join()}`,
          );
      }

      return true;
    });
  }

  return (value: unknown) => {
    schema.validateSync(value, { strict: true });
  };
};

const handRolledValidator = (
  type: Readonly<ObjectType>,
  options?: ExtendedTypeValidatorOptions,
) => {
  const validatorMap = buildPropertyValidators(type, options);

  return (value: unknown) => {
    if (!isPlainObject(value)) {
      throw typeMismatchError("object", value);
    }

    const record = value as Record<string, unknown>;

    if (!type.properties) return;

    for (const [field, validate] of Object.entries(validatorMap)) {
      try {
        validate(record[field]);
      } catch (e) {
        throw new Error(
          `invalid value in property ${field}: ${errorMessage(e)}`,
        );
      }
    }

    if (type.strict) {
      const invalidFields: string[] = [];
      for (const field in record) {
        if (!type.properties[field]) invalidFields.push(field);
      }

      if (invalidFields.length) {
        throw new Error(
          `unknown object ${
            invalidFields.length === 1 ? "property" : "properties"
          }: ${invalidFields.join()}`,
        );
      }
    }
  };
};
